import React, { useState, createElement } from 'react';
import { useMediaQuery } from 'react-responsive';
import { useTheme } from 'styled-components';
import { Row, Col } from 'reactstrap';
import ReactHtmlParser from 'react-html-parser';
import { useIntl } from 'react-intl';
import { transformLink } from '@whitelabel/helpers/i18n';
import TrustpilotStars from '@whitelabel/media/images/trustpilot-4-and-half-stars.svg';
import TrustpilotLogo from '@whitelabel/media/images/trustpilot-logo.svg';
import ProtectionTickIcon from '@whitelabel/media/icons/small/protection-tick.svg';
import GreenTickIcon from '@whitelabel/media/icons/small/green-tick.svg';
import XcoverLogo from '@whitelabel/media/logo.svg';
import { useResponsiveBackgroundImage } from '@whitelabel/helpers/useResponsiveBackgroundImage';
import { IImageBlock, IXCMSImage, Alignment } from '@whitelabel/helpers/types';
import { StyledTitle } from '../HeadingBlock/styled';
import ButtonsBlock from '../ButtonsBlock';
import {
  StyledBGContainer,
  StyledImagesBlock,
  StyledContainer,
  StyledTitleContainer,
  StyledCol,
  StyledWrapper,
  StyledLeadText,
  StyledAdditionalComponentWrapper,
  StyledCTABlock,
  StyledTrustpilotBlock,
  StyledCallout,
  StyledCalloutListItem,
  StyledCalloutLowerText,
  StyledCalloutHeading,
  StyledBgContentContainer,
} from './styled';
import DropdownLinkBlock from './DropdownLinksBlock';
import ConnectedGeoMultiQuotes from './GeoMultiQuotes';
import messages from './messages';

interface IHeroCalloutBlock {
  type: string;
  value: {
    title: string;
    listItems: {
      listType: 'unordered' | 'ordered' | 'inclusions' | 'exclusions';
      items: string[];
    };
  };
  id: string;
}

interface IHeroBlockProps {
  title: string;
  leadText?: string;
  backgroundImage?: IXCMSImage;
  supportingImages?: {
    alignment?: Alignment;
    images?: IImageBlock[];
  };
  /*
    (ts-migrate) TODO: Migrate the remaining prop types
    ...contentBlockPropTypes
    */
  ctaOptions: any[];
  className?: string;
  headingWidth?: string;
  heroCalloutOptions?: IHeroCalloutBlock[];
  trustpilotCounter?: string;
  additionalComponent?: {
    id: string;
    type: string;
    value?: {
      component?: string;
      width?: string;
    };
  }[];
  id?: string;
  hero2021?: boolean;
}

const HeroBlock = ({
  title,
  leadText = '',
  backgroundImage,
  supportingImages,
  ctaOptions,
  className,
  id = '',
  headingWidth = '7',
  additionalComponent = [],
  heroCalloutOptions,
  trustpilotCounter,
  hero2021,
}: IHeroBlockProps): JSX.Element => {
  const [userEnteredPostcode, setUserEnteredPostcode] = useState();
  const intl = useIntl();
  const theme = useTheme();
  const { formatMessage } = useIntl();
  const hasAdditionalComponent = additionalComponent && additionalComponent.length > 0;
  const heroCallout = (heroCalloutOptions?.length && heroCalloutOptions[0].value) || null;
  const isMobile = !useMediaQuery({ minWidth: !hero2021 ? theme.gridBreakpoints.sm : theme.gridBreakpoints.md });

  const bgImage = useResponsiveBackgroundImage({ backgroundImage });

  const renderTitle = () => (
    <StyledTitle hasBoxShadow={true}>
      <span>{ReactHtmlParser(title, { transform: transformLink(intl) })}</span>
    </StyledTitle>
  );

  const renderLeadText = () => (
    <StyledLeadText $hero2021={hero2021}>
      {ReactHtmlParser(leadText, { transform: transformLink(intl) })}
    </StyledLeadText>
  );

  const renderCTAOptions = () => (
    <StyledWrapper $isCtaOptions={true} $hero2021={hero2021}>
      {ctaOptions.map(({ type, value }: any) =>
        type === 'dropdown_links' ? (
          <DropdownLinkBlock key={type} {...value} />
        ) : (
          <ButtonsBlock key={type} {...value} />
        ),
      )}
    </StyledWrapper>
  );

  const renderAdditionalComponent = () => {
    // @ts-ignore FIXME. Manually added. REMOVE comment to see the full error.
    const { component } = additionalComponent[0].value;
    switch (component) {
      case 'multi_quotes':
        return (
          <StyledAdditionalComponentWrapper>
            <ConnectedGeoMultiQuotes
              // @ts-ignore ts-migrate(2322) FIXME: Type '{ userEnteredPostcode: undefined; setUserEnt... Remove this comment to see the full error message
              userEnteredPostcode={userEnteredPostcode}
              setUserEnteredPostcode={setUserEnteredPostcode}
            />
          </StyledAdditionalComponentWrapper>
        );
      default:
        return null;
    }
  };

  const renderImagesBlock = () =>
    supportingImages &&
    supportingImages.images &&
    !!supportingImages.images.length && <StyledImagesBlock $hero2021={hero2021} images={supportingImages.images} />;

  const renderTrustpilot = () => {
    if (!trustpilotCounter) return null;
    return (
      <StyledTrustpilotBlock>
        <div className="logo">
          <img src={TrustpilotLogo} alt={formatMessage(messages.trustpilotLogoAltText)} />
        </div>
        <div className="stars">
          <img src={TrustpilotStars} alt={formatMessage(messages.trustpilotStarsAltText)} />
        </div>
        <span>
          {formatMessage(messages.trustpilotReviews)} {trustpilotCounter} •{' '}
          {formatMessage(messages.trustpilotExcellent)}
        </span>
      </StyledTrustpilotBlock>
    );
  };

  const renderMobileDOM = () => {
    const renderContent = () => (
      <>
        {hasAdditionalComponent && renderAdditionalComponent()}
        {renderLeadText()}
        {!!ctaOptions.length && renderCTAOptions()}
        {!hero2021 && renderImagesBlock()}
        {hero2021 && renderTrustpilot()}
      </>
    );
    return (
      <div id={id} className={className}>
        <StyledBGContainer $rtl={intl.bidi} $backgroundImageUrl={bgImage || null} $hero2021={hero2021}>
          <StyledTitleContainer $hero2021={hero2021}>
            {hero2021 && renderImagesBlock()}
            <div className={hero2021 ? 'hero-2021' : ''}>{renderTitle()}</div>
          </StyledTitleContainer>
          {hero2021 && <StyledBgContentContainer>{renderContent()}</StyledBgContentContainer>}
        </StyledBGContainer>
        {!hero2021 && <StyledContainer>{renderContent()}</StyledContainer>}
      </div>
    );
  };

  const renderCallout = () => {
    if (!heroCallout) return null;
    const {
      title,
      listItems: { listType, items },
    } = heroCallout;
    const listEl = listType === 'ordered' ? 'ol' : 'ul';
    const renderListItems = items.map((item, index) => (
      <StyledCalloutListItem key={`callout-item-${index}`}>
        <img src={GreenTickIcon} alt={formatMessage(messages.calloutGreenTick)} />
        <span>{item}</span>
      </StyledCalloutListItem>
    ));
    return (
      <StyledCallout>
        {title && (
          <StyledCalloutHeading>
            <span>{title}</span>
            <img src={ProtectionTickIcon} alt={formatMessage(messages.calloutProtectionIcon)} />
          </StyledCalloutHeading>
        )}
        {createElement(listEl, {}, renderListItems)}
        <StyledCalloutLowerText>
          Powered by <img src={XcoverLogo} alt={formatMessage(messages.xcoverLogoAltText)} />
        </StyledCalloutLowerText>
      </StyledCallout>
    );
  };

  const renderOneColumnLayout = () => {
    const newHeroLeftColumnGrid = {
      md: '6',
      lg: '7',
    };
    const oldHeroLeftColumnGrid = {
      sm: '7',
    };
    const leftColumnSettings = hero2021 ? { ...newHeroLeftColumnGrid } : { ...oldHeroLeftColumnGrid };
    return (
      <>
        {!hero2021 && (
          <Row>
            <Col sm="9">{renderTitle()}</Col>
          </Row>
        )}
        <StyledWrapper as={Row} $hero2021={hero2021} $isMainContainer={true}>
          <StyledCol {...leftColumnSettings} $hero2021={hero2021} $justifyContentCenter={hero2021}>
            {hero2021 && renderTitle()}
            {renderLeadText()}
            {hero2021 && renderCTAOptions()}
            {hero2021 && renderTrustpilot()}
          </StyledCol>
          {hero2021 && (
            <StyledCol sm="6" lg="5" $hero2021={hero2021}>
              {renderImagesBlock()}
              {renderCallout()}
            </StyledCol>
          )}
        </StyledWrapper>
        {!hero2021 && (
          <Row>
            <StyledCol sm="8">
              {renderCTAOptions()}
              {renderImagesBlock()}
            </StyledCol>
          </Row>
        )}
      </>
    );
  };

  const renderWithAdditionalComp = () => (
    <Row>
      <StyledCol sm="6" lg={headingWidth} $hero2021={hero2021}>
        {renderTitle()}
        {renderLeadText()}
        {!!ctaOptions.length && <StyledCTABlock>{renderCTAOptions()}</StyledCTABlock>}
        {renderImagesBlock()}
      </StyledCol>
      {/* @ts-ignore FIXME. Manually added. REMOVE comment to see the full error. */}
      <StyledCol sm="6" lg={additionalComponent[0].value.width} $hero2021={hero2021}>
        {renderAdditionalComponent()}
      </StyledCol>
    </Row>
  );

  const renderNonMobileDOM = () => (
    <div id={id} className={className}>
      <StyledBGContainer $hero2021={hero2021} $rtl={intl.bidi} $backgroundImageUrl={bgImage || null}>
        <StyledContainer>
          {hasAdditionalComponent ? renderWithAdditionalComp() : renderOneColumnLayout()}
        </StyledContainer>
      </StyledBGContainer>
    </div>
  );

  return isMobile ? renderMobileDOM() : renderNonMobileDOM();
};

export default HeroBlock;
